import { apiClient } from './apiClient';
import { PAYMENT_ENDPOINTS } from '../constants/apiEndpoints';
import {
  validateCreateCheckoutSessionRequest,
  validateCreateCheckoutSessionResponse,
  validateSessionStatusResponse,
  validateSubscriptionDetails,
  validateUpdateSubscriptionRequest,
  validatePaymentMessageResponse,
  paymentMethodDetailsArraySchema,
  validateSetupIntentResponse,
  validateAttachPaymentMethodRequest,
  validateInvoice,
  validateInvoiceListResponse,
  validateUpcomingInvoice,
  validatePortalSessionResponse,
  validateInvoicePdfResponse,
  validatePricesResponse,
  PriceInfo,
} from '../schemas/payment';

export type { PriceInfo };

// ============================================================================
// CHECKOUT SESSION INTERFACES
// ============================================================================

export interface CreateCheckoutSessionRequest {
  priceId: string;
}

export interface CreateCheckoutSessionResponse {
  url: string;
}

export interface SessionStatusResponse {
  status: string;
  customer_email: string | null;
}

// ============================================================================
// SUBSCRIPTION INTERFACES
// ============================================================================

export type SubscriptionStatus =
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'trialing'
  | 'incomplete'
  | 'incomplete_expired'
  | 'unpaid'
  | 'paused';

export interface SubscriptionDetails {
  subscription_id: string;
  status: SubscriptionStatus;
  plan_name: string;
  plan_id: string;
  price_id: string;
  amount: number; // in cents
  currency: string;
  interval: string; // month or year
  current_period_start: number; // Unix timestamp
  current_period_end: number; // Unix timestamp
  cancel_at_period_end: boolean;
  canceled_at: number | null;
  trial_end: number | null;
  all_price_ids?: string[]; // All price IDs from all subscription items (including add-ons like model upgrades)
  total_amount?: number; // Total recurring amount in cents (base + add-ons). When set, avoids waiting on /prices for plan cost.
}

export interface UpdateSubscriptionRequest {
  new_price_id: string;
}

export interface MessageResponse {
  success: boolean;
  message: string;
}

// ============================================================================
// PAYMENT METHOD INTERFACES
// ============================================================================

export interface PaymentMethodDetails {
  id: string;
  type: string; // card, bank_account, etc.
  card_brand: string | null; // visa, mastercard, etc.
  card_last4: string | null;
  card_exp_month: number | null;
  card_exp_year: number | null;
  billing_email: string | null;
  is_default: boolean;
}

export interface SetupIntentResponse {
  client_secret: string;
}

export interface AttachPaymentMethodRequest {
  payment_method_id: string;
}

// ============================================================================
// INVOICE INTERFACES
// ============================================================================

export interface InvoiceLineItem {
  description: string;
  amount: number; // in cents
  currency: string;
  quantity: number;
}

export interface Invoice {
  id: string;
  invoice_number: string | null;
  status: string; // draft, open, paid, void, uncollectible
  amount_due: number; // in cents
  amount_paid: number; // in cents
  currency: string;
  created: number; // Unix timestamp
  due_date: number | null;
  paid_at: number | null;
  payment_method_brand: string | null;
  payment_method_last4: string | null;
  customer_email: string | null;
  invoice_pdf: string | null;
  hosted_invoice_url: string | null;
  line_items: InvoiceLineItem[];
}

export interface InvoiceListResponse {
  invoices: Invoice[];
  has_more: boolean;
  /** Total count of invoices. May be null if not provided by the API
   * (for example, when using cursor-based pagination). */
  total_count: number | null;
}

export interface UpcomingInvoice {
  amount_due: number; // in cents
  currency: string;
  period_start: number; // Unix timestamp
  period_end: number; // Unix timestamp
  next_payment_attempt: number | null;
  line_items: InvoiceLineItem[];
}

export interface InvoiceListParams {
  limit?: number;
  starting_after?: string;
  created_gte?: number; // Unix timestamp
  created_lte?: number; // Unix timestamp
}

export interface PortalSessionResponse {
  url: string;
}

// ============================================================================
// PAYMENT SERVICE
// ============================================================================

/**
 * Helper function to create a checkout session with validation
 * @param priceId - The Stripe price ID for the selected plan
 * @param endpoint - The API endpoint to call
 * @param errorPrefix - Prefix for error messages (e.g., "checkout session" or "enterprise checkout session")
 * @returns Promise with the checkout URL
 */
const createCheckoutSessionWithValidation = async (
  priceId: string,
  endpoint: string,
  errorPrefix: string,
): Promise<string> => {
  // Validate request payload
  const requestValidation = validateCreateCheckoutSessionRequest({ priceId });
  if (!requestValidation.success) {
    throw new Error(
      `Invalid ${errorPrefix} request: ${requestValidation.error.issues.map((e) => e.message).join(', ')}`,
    );
  }

  const response = await apiClient.post<CreateCheckoutSessionResponse>(
    endpoint,
    requestValidation.data,
  );
  const validation = validateCreateCheckoutSessionResponse(response.data);
  if (!validation.success) {
    throw new Error(
      `Invalid ${errorPrefix} response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
    );
  }
  return validation.data.url;
};

/**
 * Helper function to get payment methods with validation
 * @param endpoint - The API endpoint to call
 * @param errorPrefix - Prefix for error messages (e.g., "payment methods" or "enterprise payment methods")
 * @returns Promise with array of validated payment method details
 */
const getPaymentMethodsWithValidation = async (
  endpoint: string,
  errorPrefix: string,
): Promise<PaymentMethodDetails[]> => {
  const response = await apiClient.get<PaymentMethodDetails[]>(endpoint);
  if (!Array.isArray(response.data)) {
    throw new Error(`Invalid ${errorPrefix} response: expected an array`);
  }
  const validation = paymentMethodDetailsArraySchema.safeParse(response.data);
  if (!validation.success) {
    throw new Error(
      `Invalid ${errorPrefix} response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
    );
  }
  return validation.data;
};

/**
 * Payment service for Stripe integration
 * Handles checkout sessions, subscriptions, payment methods, and invoices
 */
export const paymentService = {
  // ==========================================================================
  // CHECKOUT SESSION METHODS
  // ==========================================================================

  /**
   * Create a Stripe checkout session
   * @param priceId - The Stripe price ID for the selected plan
   * @returns Promise with the checkout URL
   */
  async createCheckoutSession(priceId: string): Promise<string> {
    return createCheckoutSessionWithValidation(
      priceId,
      PAYMENT_ENDPOINTS.CREATE_CHECKOUT_SESSION,
      'checkout session',
    );
  },

  /**
   * Get all active Stripe prices
   * @returns Promise with array of price info
   */
  async getPrices(): Promise<PriceInfo[]> {
    const response = await apiClient.get<{ prices: PriceInfo[] }>(PAYMENT_ENDPOINTS.LIST_PRICES);
    const validation = validatePricesResponse(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid prices response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data.prices;
  },

  /**
   * Create a Stripe checkout session for organization (Enterprise billing)
   * @param priceId - The Stripe price ID for the selected plan
   * @returns Promise with the checkout URL
   */
  async createEnterpriseCheckoutSession(priceId: string): Promise<string> {
    return createCheckoutSessionWithValidation(
      priceId,
      PAYMENT_ENDPOINTS.ENTERPRISE_CREATE_CHECKOUT_SESSION,
      'enterprise checkout session',
    );
  },

  /**
   * Get the status of a checkout session
   * @param sessionId - The Stripe session ID
   * @returns Promise with session status and customer email
   */
  async getSessionStatus(sessionId: string): Promise<SessionStatusResponse> {
    const response = await apiClient.get<SessionStatusResponse>(
      `${PAYMENT_ENDPOINTS.SESSION_STATUS}?session_id=${sessionId}`,
    );
    const validation = validateSessionStatusResponse(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid session status response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  // ==========================================================================
  // SUBSCRIPTION METHODS
  // ==========================================================================

  /**
   * Get the user's current active subscription
   * @returns Promise with subscription details or null if no active subscription
   */
  async getCurrentSubscription(): Promise<SubscriptionDetails | null> {
    const response = await apiClient.get<SubscriptionDetails | null>(
      PAYMENT_ENDPOINTS.CURRENT_SUBSCRIPTION,
    );
    if (response.data === null) {
      return null;
    }
    const validation = validateSubscriptionDetails(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid subscription details response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  /**
   * Get the organization's current active subscription (accessible to all members)
   * @returns Promise with subscription details or null if no active subscription
   */
  async getOrganizationSubscription(): Promise<SubscriptionDetails | null> {
    const response = await apiClient.get<SubscriptionDetails | null>(
      PAYMENT_ENDPOINTS.ORGANIZATION_SUBSCRIPTION,
    );
    if (response.data === null) {
      return null;
    }
    const validation = validateSubscriptionDetails(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid organization subscription details response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  /**
   * Update the user's subscription to a new plan
   * @param newPriceId - The new Stripe price ID
   * @returns Promise with success message
   */
  async updateSubscription(newPriceId: string): Promise<MessageResponse> {
    // Validate request payload
    const requestValidation = validateUpdateSubscriptionRequest({ new_price_id: newPriceId });
    if (!requestValidation.success) {
      throw new Error(
        `Invalid update subscription request: ${requestValidation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }

    const response = await apiClient.put<MessageResponse>(
      PAYMENT_ENDPOINTS.UPDATE_SUBSCRIPTION,
      requestValidation.data,
    );
    const validation = validatePaymentMessageResponse(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid update subscription response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  /**
   * Cancel the user's subscription at the end of the billing period
   * @returns Promise with success message
   */
  async cancelSubscription(): Promise<MessageResponse> {
    const response = await apiClient.post<MessageResponse>(PAYMENT_ENDPOINTS.CANCEL_SUBSCRIPTION);
    const validation = validatePaymentMessageResponse(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid cancel subscription response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  // ==========================================================================
  // PAYMENT METHOD METHODS
  // ==========================================================================

  /**
   * Get all payment methods for the user
   * @returns Promise with array of payment method details
   */
  async getPaymentMethods(): Promise<PaymentMethodDetails[]> {
    return getPaymentMethodsWithValidation(PAYMENT_ENDPOINTS.PAYMENT_METHODS, 'payment methods');
  },

  /**
   * Create a setup intent for adding a new payment method
   * @returns Promise with client secret for Stripe Elements
   */
  async createSetupIntent(): Promise<string> {
    const response = await apiClient.post<SetupIntentResponse>(PAYMENT_ENDPOINTS.SETUP_INTENT);
    const validation = validateSetupIntentResponse(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid setup intent response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data.client_secret;
  },

  /**
   * Attach a payment method to the customer
   * @param paymentMethodId - The Stripe payment method ID
   * @returns Promise with success message
   */
  async attachPaymentMethod(paymentMethodId: string): Promise<MessageResponse> {
    // Validate request payload
    const requestValidation = validateAttachPaymentMethodRequest({
      payment_method_id: paymentMethodId,
    });
    if (!requestValidation.success) {
      throw new Error(
        `Invalid attach payment method request: ${requestValidation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }

    const response = await apiClient.post<MessageResponse>(
      PAYMENT_ENDPOINTS.ATTACH_PAYMENT_METHOD,
      requestValidation.data,
    );
    const validation = validatePaymentMessageResponse(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid attach payment method response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  /**
   * Set a payment method as the default
   * @param paymentMethodId - The Stripe payment method ID
   * @returns Promise with success message
   */
  async setDefaultPaymentMethod(paymentMethodId: string): Promise<MessageResponse> {
    const response = await apiClient.put<MessageResponse>(
      PAYMENT_ENDPOINTS.SET_DEFAULT_PAYMENT_METHOD(paymentMethodId),
    );
    const validation = validatePaymentMessageResponse(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid set default payment method response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  /**
   * Remove a payment method
   * @param paymentMethodId - The Stripe payment method ID
   * @returns Promise with success message
   */
  async removePaymentMethod(paymentMethodId: string): Promise<MessageResponse> {
    const response = await apiClient.delete<MessageResponse>(
      PAYMENT_ENDPOINTS.REMOVE_PAYMENT_METHOD(paymentMethodId),
    );
    const validation = validatePaymentMessageResponse(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid remove payment method response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  // ==========================================================================
  // INVOICE METHODS
  // ==========================================================================

  /**
   * List invoices with pagination and optional date filtering
   * @param params - Optional query parameters for filtering and pagination
   * @returns Promise with paginated invoice list
   */
  async getInvoices(params?: InvoiceListParams): Promise<InvoiceListResponse> {
    const queryParams = new URLSearchParams();

    if (params?.limit) {
      queryParams.append('limit', params.limit.toString());
    }
    if (params?.starting_after) {
      queryParams.append('starting_after', params.starting_after);
    }
    if (params?.created_gte) {
      queryParams.append('created_gte', params.created_gte.toString());
    }
    if (params?.created_lte) {
      queryParams.append('created_lte', params.created_lte.toString());
    }

    const response = await apiClient.get<InvoiceListResponse>(PAYMENT_ENDPOINTS.INVOICES, {
      params: queryParams,
    });
    const validation = validateInvoiceListResponse(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid invoice list response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  /**
   * Get the upcoming invoice for the user's subscription
   * @returns Promise with upcoming invoice details or null
   */
  async getUpcomingInvoice(): Promise<UpcomingInvoice | null> {
    const response = await apiClient.get<UpcomingInvoice | null>(
      PAYMENT_ENDPOINTS.UPCOMING_INVOICE,
    );
    if (response.data === null) {
      return null;
    }
    const validation = validateUpcomingInvoice(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid upcoming invoice response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  /**
   * Get details of a specific invoice
   * @param invoiceId - The Stripe invoice ID
   * @returns Promise with invoice details
   */
  async getInvoice(invoiceId: string): Promise<Invoice> {
    const response = await apiClient.get<Invoice>(PAYMENT_ENDPOINTS.INVOICE_DETAILS(invoiceId));
    const validation = validateInvoice(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid invoice response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  /**
   * Get invoice PDF URL
   * Returns the authenticated PDF URL for the invoice
   * @param invoiceId - The Stripe invoice ID
   * @returns Promise with the PDF URL
   */
  async getInvoicePDFUrl(invoiceId: string): Promise<string> {
    // Make authenticated request to get the PDF URL
    const response = await apiClient.get<{ pdf_url: string }>(
      PAYMENT_ENDPOINTS.INVOICE_PDF(invoiceId),
    );

    const validation = validateInvoicePdfResponse(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid invoice PDF response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }

    if (!validation.data.pdf_url) {
      throw new Error('PDF URL not available');
    }

    return validation.data.pdf_url;
  },

  // ==========================================================================
  // BILLING PORTAL METHODS
  // ==========================================================================

  /**
   * Create a Stripe Billing Portal session
   * Redirects user to Stripe's hosted portal for managing billing
   * @returns Promise with portal URL
   */
  async createPortalSession(): Promise<string> {
    const response = await apiClient.post<PortalSessionResponse>(
      PAYMENT_ENDPOINTS.CREATE_PORTAL_SESSION,
    );
    const validation = validatePortalSessionResponse(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid portal session response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data.url;
  },

  /**
   * Create a Stripe Billing Portal session for subscription upgrade
   * Redirects user directly to subscription update flow in Stripe's hosted portal
   * @returns Promise with portal URL
   */
  async createPortalSessionUpgrade(): Promise<string> {
    const response = await apiClient.post<PortalSessionResponse>(
      PAYMENT_ENDPOINTS.CREATE_PORTAL_SESSION_UPGRADE,
    );
    const validation = validatePortalSessionResponse(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid portal session upgrade response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data.url;
  },

  // ==========================================================================
  // ENTERPRISE BILLING METHODS (Organization-level)
  // ==========================================================================

  /**
   * Get the organization's current active subscription (Enterprise billing)
   * @returns Promise with subscription details or null if no active subscription
   */
  async getEnterpriseCurrentSubscription(): Promise<SubscriptionDetails | null> {
    const response = await apiClient.get<SubscriptionDetails | null>(
      PAYMENT_ENDPOINTS.ENTERPRISE_CURRENT_SUBSCRIPTION,
    );
    if (response.data === null) {
      return null;
    }
    const validation = validateSubscriptionDetails(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid enterprise subscription details response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  /**
   * Update the organization's subscription to a new plan (Enterprise billing)
   * @param newPriceId - The new Stripe price ID
   * @returns Promise with success message
   */
  async updateEnterpriseSubscription(newPriceId: string): Promise<MessageResponse> {
    const requestValidation = validateUpdateSubscriptionRequest({ new_price_id: newPriceId });
    if (!requestValidation.success) {
      throw new Error(
        `Invalid update enterprise subscription request: ${requestValidation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }

    const response = await apiClient.put<MessageResponse>(
      PAYMENT_ENDPOINTS.ENTERPRISE_UPDATE_SUBSCRIPTION,
      requestValidation.data,
    );
    const validation = validatePaymentMessageResponse(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid update enterprise subscription response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  /**
   * Cancel the organization's subscription at the end of the billing period (Enterprise billing)
   * @returns Promise with success message
   */
  async cancelEnterpriseSubscription(): Promise<MessageResponse> {
    const response = await apiClient.post<MessageResponse>(
      PAYMENT_ENDPOINTS.ENTERPRISE_CANCEL_SUBSCRIPTION,
    );
    const validation = validatePaymentMessageResponse(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid cancel enterprise subscription response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  /**
   * Get all payment methods for the organization (Enterprise billing)
   * @returns Promise with array of payment method details
   */
  async getEnterprisePaymentMethods(): Promise<PaymentMethodDetails[]> {
    return getPaymentMethodsWithValidation(
      PAYMENT_ENDPOINTS.ENTERPRISE_PAYMENT_METHODS,
      'enterprise payment methods',
    );
  },

  /**
   * Create a setup intent for adding a new payment method to the organization (Enterprise billing)
   * @returns Promise with client secret for Stripe Elements
   */
  async createEnterpriseSetupIntent(): Promise<string> {
    const response = await apiClient.post<SetupIntentResponse>(
      PAYMENT_ENDPOINTS.ENTERPRISE_SETUP_INTENT,
    );
    const validation = validateSetupIntentResponse(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid enterprise setup intent response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data.client_secret;
  },

  /**
   * Attach a payment method to the organization (Enterprise billing)
   * @param paymentMethodId - The Stripe payment method ID
   * @returns Promise with success message
   */
  async attachEnterprisePaymentMethod(paymentMethodId: string): Promise<MessageResponse> {
    const requestValidation = validateAttachPaymentMethodRequest({
      payment_method_id: paymentMethodId,
    });
    if (!requestValidation.success) {
      throw new Error(
        `Invalid attach enterprise payment method request: ${requestValidation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }

    const response = await apiClient.post<MessageResponse>(
      PAYMENT_ENDPOINTS.ENTERPRISE_ATTACH_PAYMENT_METHOD,
      requestValidation.data,
    );
    const validation = validatePaymentMessageResponse(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid attach enterprise payment method response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  /**
   * Set a payment method as the default for the organization (Enterprise billing)
   * @param paymentMethodId - The Stripe payment method ID
   * @returns Promise with success message
   */
  async setEnterpriseDefaultPaymentMethod(paymentMethodId: string): Promise<MessageResponse> {
    const requestValidation = validateAttachPaymentMethodRequest({
      payment_method_id: paymentMethodId,
    });
    if (!requestValidation.success) {
      throw new Error(
        `Invalid set enterprise default payment method request: ${requestValidation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }

    const response = await apiClient.post<MessageResponse>(
      PAYMENT_ENDPOINTS.ENTERPRISE_SET_DEFAULT_PAYMENT_METHOD,
      requestValidation.data,
    );
    const validation = validatePaymentMessageResponse(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid set enterprise default payment method response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  /**
   * Remove a payment method from the organization (Enterprise billing)
   * @param paymentMethodId - The Stripe payment method ID
   * @returns Promise with success message
   */
  async removeEnterprisePaymentMethod(paymentMethodId: string): Promise<MessageResponse> {
    const response = await apiClient.delete<MessageResponse>(
      PAYMENT_ENDPOINTS.ENTERPRISE_REMOVE_PAYMENT_METHOD(paymentMethodId),
    );
    const validation = validatePaymentMessageResponse(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid remove enterprise payment method response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  /**
   * List invoices for the organization with pagination and optional date filtering (Enterprise billing)
   * @param params - Optional query parameters for filtering and pagination
   * @returns Promise with paginated invoice list
   */
  async getEnterpriseInvoices(params?: InvoiceListParams): Promise<InvoiceListResponse> {
    const queryParams = new URLSearchParams();

    if (params?.limit) {
      queryParams.append('limit', params.limit.toString());
    }
    if (params?.starting_after) {
      queryParams.append('starting_after', params.starting_after);
    }
    if (params?.created_gte) {
      queryParams.append('created_gte', params.created_gte.toString());
    }
    if (params?.created_lte) {
      queryParams.append('created_lte', params.created_lte.toString());
    }

    const response = await apiClient.get<InvoiceListResponse>(
      PAYMENT_ENDPOINTS.ENTERPRISE_INVOICES,
      {
        params: queryParams,
      },
    );
    const validation = validateInvoiceListResponse(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid enterprise invoice list response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  /**
   * Get the upcoming invoice for the organization's subscription (Enterprise billing)
   * @returns Promise with upcoming invoice details or null
   */
  async getEnterpriseUpcomingInvoice(): Promise<UpcomingInvoice | null> {
    const response = await apiClient.get<UpcomingInvoice | null>(
      PAYMENT_ENDPOINTS.ENTERPRISE_UPCOMING_INVOICE,
    );
    if (response.data === null) {
      return null;
    }
    const validation = validateUpcomingInvoice(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid enterprise upcoming invoice response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  /**
   * Get details of a specific organization invoice (Enterprise billing)
   * @param invoiceId - The Stripe invoice ID
   * @returns Promise with invoice details
   */
  async getEnterpriseInvoice(invoiceId: string): Promise<Invoice> {
    const response = await apiClient.get<Invoice>(
      PAYMENT_ENDPOINTS.ENTERPRISE_INVOICE_DETAILS(invoiceId),
    );
    const validation = validateInvoice(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid enterprise invoice response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  /**
   * Get organization invoice PDF URL (Enterprise billing)
   * Returns the authenticated PDF URL for the organization invoice
   * @param invoiceId - The Stripe invoice ID
   * @returns Promise with the PDF URL
   */
  async getEnterpriseInvoicePdfUrl(invoiceId: string): Promise<string> {
    const response = await apiClient.get<{ pdf_url: string }>(
      PAYMENT_ENDPOINTS.ENTERPRISE_INVOICE_PDF(invoiceId),
    );

    const validation = validateInvoicePdfResponse(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid enterprise invoice PDF response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }

    if (!validation.data.pdf_url) {
      throw new Error('PDF URL not available');
    }

    return validation.data.pdf_url;
  },

  /**
   * Create a Stripe Billing Portal session for the organization (Enterprise billing)
   * Redirects user to Stripe's hosted portal for managing organization billing
   * @returns Promise with portal URL
   */
  async createEnterprisePortalSession(): Promise<string> {
    const response = await apiClient.post<PortalSessionResponse>(
      PAYMENT_ENDPOINTS.ENTERPRISE_CREATE_PORTAL_SESSION,
    );
    const validation = validatePortalSessionResponse(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid enterprise portal session response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data.url;
  },

  /**
   * Create a Stripe Billing Portal session for organization subscription upgrade (Enterprise billing)
   * Redirects user directly to subscription update flow in Stripe's hosted portal
   * @returns Promise with portal URL
   */
  async createEnterprisePortalSessionUpgrade(): Promise<string> {
    const response = await apiClient.post<PortalSessionResponse>(
      PAYMENT_ENDPOINTS.ENTERPRISE_CREATE_PORTAL_SESSION_UPGRADE,
    );
    const validation = validatePortalSessionResponse(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid enterprise portal session upgrade response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data.url;
  },
};
